/**
 * Search highlighting for the renderers that produce ordinary DOM (#627).
 *
 * The PDF renderer has its own path: react-pdf builds a text layer and hands
 * each item back as an HTML string, so it can splice a `<mark>` in while
 * rendering. Markdown, code, CSV and JSON are already in the document by the
 * time we know what to look for, so their hits are marked afterwards by
 * walking the text nodes.
 *
 * Splitting text nodes rather than rewriting `innerHTML` matters: the rendered
 * markdown carries links and event handlers that an innerHTML round-trip would
 * throw away, and re-parsing user content as HTML is how injection bugs start.
 */

export const HIT_CLASS = 'preview-search-hit'

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** Puts the marked text back the way it was, so a new term starts from clean DOM. */
export function clearHighlights(root: HTMLElement): void {
  root.querySelectorAll(`mark.${HIT_CLASS}`).forEach((mark) => {
    const parent = mark.parentNode
    if (!parent) return
    parent.replaceChild(root.ownerDocument.createTextNode(mark.textContent ?? ''), mark)
    parent.normalize()
  })
}

/**
 * Wraps every case-insensitive occurrence of `term` inside `root` and returns
 * how many were found. An empty term only clears.
 */
export function highlightWithin(root: HTMLElement, term: string): number {
  clearHighlights(root)
  const needle = term.trim()
  if (!needle) return 0

  const doc = root.ownerDocument
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const parent = (node as Text).parentElement
    if (!parent) continue
    if (parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE') continue
    textNodes.push(node as Text)
  }

  let count = 0
  for (const node of textNodes) {
    const value = node.nodeValue ?? ''
    // A fresh regex per node — a shared global one carries lastIndex across
    // nodes and silently skips the first hit of the next one.
    const pattern = new RegExp(escapeRegExp(needle), 'gi')
    let match = pattern.exec(value)
    if (!match) continue

    const fragment = doc.createDocumentFragment()
    let cursor = 0
    while (match) {
      fragment.appendChild(doc.createTextNode(value.slice(cursor, match.index)))
      const mark = doc.createElement('mark')
      mark.className = HIT_CLASS
      mark.textContent = match[0]
      fragment.appendChild(mark)
      cursor = match.index + match[0].length
      count += 1
      match = pattern.exec(value)
    }
    fragment.appendChild(doc.createTextNode(value.slice(cursor)))
    node.parentNode?.replaceChild(fragment, node)
  }
  return count
}

/** The first marked hit, for scrolling it into view. */
export function firstHit(root: HTMLElement): HTMLElement | null {
  return root.querySelector(`mark.${HIT_CLASS}`)
}
